<script setup lang="ts">
import { Button } from '@/components/ui/button';
import ArrowIcon from '@/components/icons/ArrowIcon.vue';
import { ref } from 'vue';

const revealed = ref(false);

defineProps<{ title: string }>();

const handleClick = (event: MouseEvent) => {
  // Don't toggle if clicking on a link
  if ((event.target as Element).closest('a')) {
    event.stopPropagation();
    return;
  }
  revealed.value = !revealed.value;
};
</script>

<template>
  <section class="image-card" :data-revealed="revealed" @click="handleClick">
    <slot name="bg" />

    <div class="cover">
      <div class="title-wrapper">
        <h1>{{ title }}</h1>
      </div>

      <Button variant="outline" size="xl" class="rounded-md group">
        <span>View</span>
        <ArrowIcon />
      </Button>
    </div>

    <div class="content">
      <div class="overlay" />
      <div class="content-inner">
        <div class="flex flex-col items-center text-center">
          <h2 class="text-2xl font-bold mb-6">{{ title }}</h2>
          <slot />
        </div>
      </div>
    </div>
  </section>
</template>

<style>
.image-card {
  @apply relative flex items-center justify-center text-white bg-black rounded-xl overflow-hidden cursor-pointer shadow-md transition-all duration-500 ease-out min-h-[400px];
  transform: translateZ(0);

  &:hover {
    @apply shadow-lg;
    transform: translateZ(0) scale(1.01);
  }

  > img,
  > video {
    @apply absolute inset-0 object-cover w-full h-full opacity-90 transition-all duration-500 ease-out;
  }

  > .cover {
    @apply absolute inset-0 select-none flex flex-col gap-20 items-center justify-center rounded-xl transition-opacity duration-500 ease-out;
    background: linear-gradient(180deg, 
      transparent 0%, 
      rgba(0,0,0,0.1) 50%,
      rgba(0,0,0,0.2) 100%
    );

    .title-wrapper {
      @apply px-6 py-2 transition-transform duration-500 ease-out;
      z-index: 1;
    }

    h1 {
      @apply text-5xl md:text-7xl font-bold text-center text-shadow transition-all duration-500 ease-out;
      letter-spacing: -0.02em;
      line-height: 1;
    }

    > button {
      @apply text-xl bg-black/20 hover:bg-black/30 transition-all duration-500 ease-out;
      z-index: 1;
    }

    &:hover {
      background: linear-gradient(180deg, 
        transparent 0%, 
        rgba(0,0,0,0.15) 50%,
        rgba(0,0,0,0.25) 100%
      );

      > button {
        @apply scale-[1.02];
      }
      
      .title-wrapper {
        transform: scale(1.02);
      }
    }
  }

  > .content {
    @apply absolute inset-0 p-8 text-lg opacity-0 pointer-events-none transition-opacity duration-500 ease-out flex items-center justify-center;
    z-index: 1;

    .overlay {
      @apply absolute inset-[-1px] bg-black/60 backdrop-blur-[1px];
      z-index: -1;
    }
  }

  .content-inner {
    @apply flex flex-col relative w-full max-w-lg;
    z-index: 1;
  }

  &[data-revealed='true'] {
    > img,
    > video {
      @apply opacity-40;
      transform: scale(1.02);
    }

    > .cover {
      @apply opacity-0 pointer-events-none;
    }

    > .content {
      @apply opacity-100 pointer-events-auto;
    }
  }
}

.text-shadow {
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
}
</style>
